import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { crmOpportunityApi, crmPipelineStageApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import PipelineBoard from './components/PipelineBoard';
import './CRM.css';

const STAGE_OPTIONS = ['New', 'Contacted', 'Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation', 'Won', 'Lost'].map((s) => ({ value: s, label: s }));

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const fallbackOpps = [
  { id: 1, opportunityId: 'OPP-1041', customerName: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', expectedValue: 82500, stage: 'Negotiation', salesperson: 'Rahul Sharma', probability: 70, expectedCloseDate: '2026-10-20', product: 'Oxygen Cylinder', quantity: 50 },
  { id: 2, opportunityId: 'OPP-1042', customerName: 'Rajesh Kumar', company: 'Kumar Industrial Gases', expectedValue: 145000, stage: 'Quotation', salesperson: 'Priya Verma', probability: 50, expectedCloseDate: '2026-11-05', product: 'LPG Cylinder', quantity: 100 },
  { id: 3, opportunityId: 'OPP-1043', customerName: 'Sunita Devi', company: 'Devi Gas Agency', expectedValue: 62000, stage: 'Qualified', salesperson: 'Amit Kumar', probability: 40, expectedCloseDate: '2026-10-15', product: 'Acetylene Cylinder', quantity: 20 },
  { id: 4, opportunityId: 'OPP-1044', customerName: 'Pooja Sharma', company: 'Sharma Medical Gas', expectedValue: 195000, stage: 'Negotiation', salesperson: 'Sneha Patel', probability: 65, expectedCloseDate: '2026-10-12', product: 'Medical Oxygen Cylinder', quantity: 80 },
  { id: 5, opportunityId: 'OPP-1045', customerName: 'Deepak Joshi', company: 'Joshi Fabricators', expectedValue: 78000, stage: 'Won', salesperson: 'Rahul Sharma', probability: 100, expectedCloseDate: '2026-10-01', product: 'CO2 Cylinder', quantity: 40 },
  { id: 6, opportunityId: 'OPP-1046', customerName: 'Mohammed Ali', company: 'Ali Welding Works', expectedValue: 45000, stage: 'New', salesperson: 'Rahul Sharma', probability: 10, expectedCloseDate: '2026-11-20', product: 'Argon Cylinder', quantity: 30 },
  { id: 7, opportunityId: 'OPP-1047', customerName: 'Kailash Verma', company: 'Verma Tools & Dies', expectedValue: 96000, stage: 'Contacted', salesperson: 'Priya Verma', probability: 25, expectedCloseDate: '2026-11-10', product: 'Nitrogen Cylinder', quantity: 45 },
  { id: 8, opportunityId: 'OPP-1048', customerName: 'Neha Gupta', company: 'Gupta Pharma Gas', expectedValue: 120000, stage: 'Requirement', salesperson: 'Sneha Patel', probability: 45, expectedCloseDate: '2026-10-28', product: 'Medical Oxygen Cylinder', quantity: 60 },
  { id: 9, opportunityId: 'OPP-1049', customerName: 'Imran Khan', company: 'Khan Fabrication', expectedValue: 38000, stage: 'Interested', salesperson: 'Amit Kumar', probability: 30, expectedCloseDate: '2026-11-15', product: 'Argon Cylinder', quantity: 25 },
  { id: 10, opportunityId: 'OPP-1050', customerName: 'Lalit Bansal', company: 'Bansal Steel Traders', expectedValue: 154000, stage: 'Lost', salesperson: 'Rahul Sharma', probability: 0, expectedCloseDate: '2026-09-28', product: 'Oxygen Cylinder', quantity: 90 },
];

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

export default function CRMPipeline() {
  const navigate = useNavigate();
  const [opps, setOpps] = useState(fallbackOpps);
  const [view, setView] = useState('board'); // board | table
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o] = await Promise.allSettled([crmOpportunityApi.getAll({ size: 200 }), crmPipelineStageApi.getAll()]);
      if (o.status === 'fulfilled') {
        const d = Array.isArray(o.value?.data) ? o.value.data : o.value?.data?.content;
        if (d?.length) setOpps(d);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStageChange = async (oppId, stage) => {
    const opp = opps.find((o) => o.id === oppId);
    setOpps((p) => p.map((o) => o.id === oppId ? { ...o, stage } : o));
    try {
      await crmOpportunityApi.updateStage(oppId, { stage });
      toast.success(`Moved ${opp?.opportunityId || ''} → ${stage}`);
    } catch { toast.error('Failed to update stage.'); fetchAll(); }
  };

  const openCard = (opp) => navigate(`/crm/opportunities/${opp.id}`);

  const pipelineValue = opps.filter((o) => !['Won', 'Lost'].includes(o.stage)).reduce((s, o) => s + Number(o.expectedValue || 0), 0);
  const wonValue = opps.filter((o) => o.stage === 'Won').reduce((s, o) => s + Number(o.expectedValue || 0), 0);

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon">🗂</div>
          <div>
            <h1 className="crm-title">Sales Pipeline</h1>
            <p className="crm-subtitle">Drag & drop opportunities across stages — Won deals become Sales Orders</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <div className="crm-btn-group">
            <button className={`crm-btn crm-btn-sm ${view === 'board' ? 'crm-btn-primary' : 'crm-btn-outline'}`} onClick={() => setView('board')}>▦ Board</button>
            <button className={`crm-btn crm-btn-sm ${view === 'table' ? 'crm-btn-primary' : 'crm-btn-outline'}`} onClick={() => setView('table')}>☰ Table</button>
          </div>
          <button className="crm-btn crm-btn-ghost" onClick={fetchAll}>🔄 Refresh</button>
          <button className="crm-btn crm-btn-primary" onClick={() => navigate('/crm/opportunities/new')}>+ New Opportunity</button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {[
          { label: 'Open Pipeline Value', value: money(pipelineValue), color: '#6366F1' },
          { label: 'Open Opportunities', value: opps.filter((o) => !['Won', 'Lost'].includes(o.stage)).length, color: '#0EA5E9' },
          { label: 'Won Value', value: money(wonValue), color: '#16A34A' },
          { label: 'Total Opportunities', value: opps.length, color: '#8B5CF6' },
        ].map((k) => (
          <div className="crm-kpi-card" key={k.label}>
            <div><div className="crm-kpi-value" style={{ color: k.color }}>{k.value}</div><div className="crm-kpi-label">{k.label}</div></div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="crm-kanban">
          {Array.from({ length: 5 }).map((_, i) => (
            <div className="crm-kanban-col" key={`skeleton-${i}`}>
              <div className="crm-kanban-col-head">
                <SkeletonBlock width={80} height={14} radius={6} />
                <SkeletonBlock width={24} height={18} radius={6} />
              </div>
              <div className="crm-kanban-col-body">
                <SkeletonBlock width="100%" height={92} radius={10} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="100%" height={92} radius={10} />
              </div>
            </div>
          ))}
        </div>
      ) : view === 'board' ? (
        <PipelineBoard opportunities={opps} onStageChange={handleStageChange} onOpenCard={openCard} />
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead><tr><th>Opportunity</th><th>Customer</th><th>Product</th><th>Value</th><th>Stage</th><th>Probability</th><th>Close Date</th><th>Salesperson</th><th>Actions</th></tr></thead>
            <tbody>
              {opps.map((o) => (
                <tr key={o.id}>
                  <td className="link-cell" onClick={() => navigate(`/crm/opportunities/${o.id}`)}>{o.opportunityId}</td>
                  <td><strong>{o.customerName}</strong><div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{o.company}</div></td>
                  <td>{o.product}{o.quantity ? ` × ${o.quantity}` : ''}</td>
                  <td style={{ fontWeight: 700 }}>{money(o.expectedValue)}</td>
                  <td>
                    <AppDropdown value={o.stage} options={STAGE_OPTIONS}
                      onChange={(v) => handleStageChange(o.id, v)}
                      menuWidth={180} menuClassName="crm-dropdown-window" />
                  </td>
                  <td>{o.probability}%</td>
                  <td>{o.expectedCloseDate || '—'}</td>
                  <td>{o.salesperson || '—'}</td>
                  <td><div className="crm-actions">
                    <button className="crm-icon-btn view" title="View" onClick={() => navigate(`/crm/opportunities/${o.id}`)}>👁</button>
                    <button className="crm-icon-btn quote" title="Create Quotation" onClick={() => navigate('/dashboard/estimates/new', { state: { crmOpportunityId: o.id, opportunity: o } })}>📄</button>
                    {o.stage === 'Won' && <button className="crm-icon-btn convert" title="Create Sales Order" onClick={() => navigate('/dashboard/sales-orders/new', { state: { crmOpportunityId: o.id, opportunity: o } })}>🧾</button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
